import DateTime from 'luxon/src/datetime'
import Base from './base'
import api from '../api/v1'
import { dateStringFormat } from '../utils'

const WEEKDAYS = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela']

const formatDecimal = (value, fractionDigits = 2) => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  return number.toLocaleString('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits
  })
}

const formatLoops = value => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  return number.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatDuration = totalSeconds => {
  if (totalSeconds === null || totalSeconds === undefined || totalSeconds === '') return null
  const seconds = Number(totalSeconds)
  if (!Number.isFinite(seconds)) return null
  const rounded = Math.round(seconds)
  const hours = Math.floor(rounded / 3600)
  const minutes = Math.floor((rounded % 3600) / 60)
  const remainingSeconds = Math.abs(rounded % 60)
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

const display = value => value ?? '-'
const formatKm = value => formatDecimal(value) && `${formatDecimal(value)} km`

// Bonus amounts are stored in grosze on the backend
const formatAmount = grosze => (grosze == null ? null : formatDecimal(Number(grosze) / 100))
const toGrosze = amount => Math.round(Number(String(amount).replace(',', '.')) * 100)

const monthDateRange = month => {
  const start = DateTime.fromISO(`${month}-01`)
  return [start.startOf('month').toISODate(), start.endOf('month').toISODate()]
}

const mapDay = day => ({
  id: day.date,
  date: day.date,
  formattedDate: dateStringFormat(day.date),
  weekday: WEEKDAYS[DateTime.fromISO(day.date).weekday - 1],
  workTime: display(formatDuration(day.workTime)),
  driveTime: display(formatDuration(day.drivingTime)),
  distance: display(formatKm(day.distanceKm)),
  loopsWithTrailer: display(formatLoops(day.trailerLoopCount)),
  loopsSolo: display(formatLoops(day.soloLoopCount)),
  bonuses: day.bonus ? `${formatAmount(day.bonus.amount)} zł` : '-',
  bonus: day.bonus,
  kmPerLoop: display(formatDecimal(day.kmPerLoop, 0)),
  driveTimePerLoop: display(formatDuration(day.drivingTimePerLoop))
})

const mapWeek = week => {
  const totals = week.totals || {}
  return {
    id: week.weekStart,
    weekStart: week.weekStart,
    weekEnd: week.weekEnd,
    days: (week.days || []).map(mapDay),
    totals: {
      avgWorkTime: display(formatDuration(totals.avgWorkTime)),
      sumDrivingTime: display(formatDuration(totals.sumDrivingTime)),
      sumDistanceKm: display(formatKm(totals.sumDistanceKm)),
      sumTrailerLoopCount: display(formatLoops(totals.sumTrailerLoopCount)),
      sumSoloLoopCount: display(formatLoops(totals.sumSoloLoopCount)),
      sumBonusAmount: totals.sumBonusAmount == null ? '-' : `${formatAmount(totals.sumBonusAmount)} zł`,
      kmPerLoop: display(formatDecimal(totals.kmPerLoop, 0)),
      drivingTimePerLoop: display(formatDuration(totals.drivingTimePerLoop))
    }
  }
}

const mapStats = (rawSummary = {}) => {
  // Backend returns 0 for empty sums but null for averages it can't compute - treat both as missing so every card shows '-'
  const summary = Object.fromEntries(Object.entries(rawSummary || {}).map(([key, value]) => [key, Number(value) ? value : null]))
  const workOutsideDrive = Math.max((summary.sumWorkTime || 0) - (summary.sumDrivingTime || 0), 0) || null
  const avgLoopCount = summary.sumLoopCount == null || !summary.workDaysCount ? null : summary.sumLoopCount / summary.workDaysCount
  const bonusPerTrailerLoop = formatAmount(summary.bonusPerTrailerLoop)

  const cards = [
    { key: 'averageWorkTime', icon: 'clock', label: 'Średni czas pracy', value: formatDuration(summary.avgWorkTime), hint: 'dziennie', composite: true, iconWidth: 64, iconHeight: 64 },
    { key: 'totalWorkTime', icon: 'clock', label: 'Suma czasu pracy', value: formatDuration(summary.sumWorkTime), hint: 'w miesiącu', composite: true, iconWidth: 64, iconHeight: 64 },
    { key: 'totalDriveTime', icon: 'car-back', label: 'Suma czasu jazdy', value: formatDuration(summary.sumDrivingTime), composite: true, iconWidth: 64, iconHeight: 64 },
    { key: 'workOutsideDrive', icon: 'timelapse', label: 'Czas pracy poza jazdą', value: formatDuration(workOutsideDrive), iconWidth: 48, iconHeight: 48 },
    { key: 'distance', icon: 'highway', label: 'Przejechane', value: formatKm(summary.sumDistanceKm), iconWidth: 40, iconHeight: 40 },
    { key: 'loopsWithTrailer', icon: 'loop', label: 'Suma pętli', value: formatLoops(summary.sumTrailerLoopCount), hint: 'z przyczepą', iconWidth: 48, iconHeight: 48 },
    { key: 'totalLoops', icon: 'loop', label: 'Suma pętli', value: formatLoops(summary.sumLoopCount), hint: 'wszystkich', iconWidth: 48, iconHeight: 48 },
    { key: 'loopsSolo', icon: 'loop', label: 'Suma pętli', value: formatLoops(summary.sumSoloLoopCount), hint: 'solo', iconWidth: 48, iconHeight: 48 },
    { key: 'bonuses', icon: 'tab-plus', label: 'Suma bonusów', value: formatAmount(summary.sumBonusAmount) && `${formatAmount(summary.sumBonusAmount)} zł`, iconWidth: 36, iconHeight: 36 },
    { key: 'kmPerLoop', icon: 'slash-forward', label: 'Kilometry / pętle', value: formatDecimal(summary.kmPerLoop, 0), iconWidth: 48, iconHeight: 48 },
    { key: 'drivePerLoop', icon: 'camera-timer', label: 'Czas jazdy / pętle', value: formatDuration(summary.drivingTimePerLoop), iconWidth: 36, iconHeight: 36 },
    { key: 'bonusPerTrailerLoop', icon: 'loop', label: 'Bonus / przyczepa', value: bonusPerTrailerLoop && `${bonusPerTrailerLoop} zł`, iconWidth: 48, iconHeight: 48 },
    { key: 'averageLoops', icon: 'loop', label: 'Średnia ilość pętli', value: formatDecimal(avgLoopCount), iconWidth: 48, iconHeight: 48 },
    { key: 'absoluteAverageLoops', icon: 'loop', label: 'Średnia ilość pętli', value: formatDecimal(summary.avgAbsoluteLoopCount), hint: 'bezwzględna', iconWidth: 48, iconHeight: 48 },
    {
      key: 'workingDays',
      icon: 'calendar-today',
      label: 'Liczba dni roboczych',
      value: summary.workDaysCount == null ? null : String(summary.workDaysCount),
      iconWidth: 43,
      iconHeight: 46,
      boxWidth: 59,
      boxHeight: 62
    },
    {
      key: 'averageSpeed',
      icon: 'car-speed-limiter',
      label: 'Średnia prędkość',
      value: formatDecimal(summary.avgSpeed, 1) && `${formatDecimal(summary.avgSpeed, 1)} km/h`,
      iconWidth: 51,
      iconHeight: 48,
      boxWidth: 67,
      boxHeight: 64
    }
  ]

  return cards.map(card => ({ ...card, value: card.value ?? '-' }))
}

const base = new Base('driversDetails')

const state = {
  summary: null,
  weeks: [],
  drivers: []
}

const mutations = {
  SET_REPORT(state, { summary, weeks }) {
    state.summary = summary
    state.weeks = weeks
  },
  SET_DRIVERS(state, drivers) {
    state.drivers = drivers
  }
}

const actions = {
  // same list as in drivers summary, so picker contains every driver reported in given month (including removed ones)
  getDrivers({ commit, rootState }, { month }) {
    const departmentId = rootState.core.department?.id
    const filters = { daterange: monthDateRange(month) }
    if (departmentId) filters.departmentIds = [departmentId]

    return api
      .getItems({
        tableName: 'driversDetails',
        params: {
          endpoint: 'reports/drivers',
          filters,
          order: { field: 'driver_full_name', direction: 'asc' }
        }
      })
      .then(response => {
        const drivers = (response.data?.drivers || []).map(driver => ({ id: driver.id, name: driver.driverFullName }))
        commit('SET_DRIVERS', drivers)
      })
  },
  clearReport({ commit }) {
    commit('SET_REPORT', { summary: null, weeks: [] })
  },
  getItems({ commit }, { driverId, month } = {}) {
    if (!driverId || !month) return Promise.resolve()

    commit('SET_PROCESSING', true)
    return api
      .getItems({
        tableName: 'driversDetails',
        params: {
          endpoint: 'reports/driver_details',
          filters: {
            daterange: monthDateRange(month),
            driverId
          }
        }
      })
      .then(response => {
        const data = response.data || {}
        commit('SET_REPORT', {
          summary: mapStats(data.summary),
          weeks: (data.weeks || []).map(mapWeek)
        })
      })
      .finally(() => {
        commit('SET_PROCESSING', false)
      })
  },
  createBonus({ commit, dispatch }, { driverId, date, amount, note, month }) {
    commit('SET_PROCESSING', true)
    return api
      .createDriverBonus({ driverId, date, amount: toGrosze(amount), note })
      .then(() => dispatch('getItems', { driverId, month }))
      .finally(() => {
        commit('SET_PROCESSING', false)
      })
  },
  updateBonus({ commit, dispatch }, { id, amount, note, driverId, month }) {
    commit('SET_PROCESSING', true)
    return api
      .updateDriverBonus(id, { amount: toGrosze(amount), note })
      .then(() => dispatch('getItems', { driverId, month }))
      .finally(() => {
        commit('SET_PROCESSING', false)
      })
  },
  deleteBonus({ commit, dispatch }, { id, driverId, month }) {
    commit('SET_PROCESSING', true)
    return api
      .deleteDriverBonus(id)
      .then(() => dispatch('getItems', { driverId, month }))
      .finally(() => {
        commit('SET_PROCESSING', false)
      })
  }
}

base.state = {
  ...base.state,
  ...state
}
base.mutations = {
  ...base.mutations,
  ...mutations
}
base.actions = {
  ...base.actions,
  ...actions
}

export default base
